import React, { useCallback, useState } from 'react';

// AI Fit Check Share Card — generated from the REAL AI recommendation output only.
// Rotating light taglines. No invented data. Fires ai_fit_share_card_created on open
// and ai_fit_share_card_shared on user action.
const TAGLINES = [
  'Mummy says IIT. My budget says: let’s talk.',
  'College list sorted. Family discussion loading.',
  'Dream big. Choose smarter.',
];

function pickTagline(seed) {
  const s = (seed || '').toString();
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return TAGLINES[hash % TAGLINES.length];
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y); ctx.lineTo(x + w - r, y); ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r); ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h); ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r); ctx.quadraticCurveTo(x, y, x + r, y); ctx.closePath();
}

function truncate(s, n) { s = (s || '').trim(); return s.length > n ? s.slice(0, n - 1) + '…' : s; }

export function generateFitShareImage({ dream, target, safe, tagline }) {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const W = 1080; const H = 1350;
    canvas.width = W; canvas.height = H;
    // Navy background + gold accents
    ctx.fillStyle = '#0B2352'; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = '#D4A24C'; ctx.fillRect(0, 0, W, 8);
    // Title chip
    ctx.fillStyle = '#D4A24C'; roundRect(ctx, 60, 60, 260, 50, 25); ctx.fill();
    ctx.fillStyle = '#0B2352'; ctx.font = 'bold 22px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillText('MY COLLEGE FIT CHECK', 82, 93);
    // Section
    ctx.fillStyle = '#FFFFFF'; ctx.font = 'bold 44px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillText('Where I could land.', 60, 190);
    ctx.fillStyle = 'rgba(255,255,255,0.6)'; ctx.font = '22px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillText('Based on my real AI Fit Check on CollegeUnlock.', 60, 226);
    // Rows: Dream / Target / Safe
    const rows = [
      { label: 'Dream', color: '#D4A24C', name: dream },
      { label: 'Target', color: '#22c55e', name: target },
      { label: 'Safe', color: '#3b82f6', name: safe },
    ];
    let y = 300;
    rows.forEach((r) => {
      ctx.fillStyle = r.color; roundRect(ctx, 60, y, W - 120, 130, 18); ctx.fill();
      ctx.fillStyle = '#0B2352'; ctx.font = 'bold 22px -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.fillText(r.label.toUpperCase(), 90, y + 42);
      ctx.font = 'bold 34px -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.fillText(truncate(r.name || 'Not available', 28), 90, y + 88);
      y += 150;
    });
    // Tagline
    ctx.fillStyle = '#FFFFFF'; ctx.font = 'italic 30px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.textAlign = 'center'; ctx.fillText('"' + tagline + '"', W / 2, y + 100); ctx.textAlign = 'left';
    // Footer
    ctx.fillStyle = '#D4A24C'; ctx.fillRect(0, H - 70, W, 70);
    ctx.fillStyle = '#0B2352'; ctx.font = 'bold 24px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.textAlign = 'center'; ctx.fillText('Get your own College Reality Check at collegeunlock.com', W / 2, H - 32); ctx.textAlign = 'left';
    // Border
    ctx.strokeStyle = '#D4A24C'; ctx.lineWidth = 4; ctx.strokeRect(2, 2, W - 4, H - 4);
    canvas.toBlob((blob) => resolve(blob), 'image/png', 1.0);
  });
}

// Extract first college name from an AI Fit Check bucket. Handles both string and object formats.
function pickCollege(bucket) {
  if (!bucket) return null;
  const first = Array.isArray(bucket) ? bucket[0] : bucket;
  if (!first) return null;
  return first.name || first.college || first.College_Name || first.title || (typeof first === 'string' ? first : null);
}

export default function AIFitShareCard({ result }) {
  const [busy, setBusy] = useState(false);
  const [opened, setOpened] = useState(false);
  const recs = result && result.recommendations ? result.recommendations : null;
  if (!recs) return null;
  const dream = pickCollege(recs.dream);
  const target = pickCollege(recs.target);
  const safe = pickCollege(recs.safe);
  // If AI produced no colleges, do not offer a share card (never invent data).
  if (!dream && !target && !safe) return null;
  const tagline = pickTagline((dream || '') + (target || '') + (safe || ''));
  const shareText = `My AI College Fit Check on CollegeUnlock:\n\nDream: ${dream || '—'}\nTarget: ${target || '—'}\nSafe: ${safe || '—'}\n\n${tagline}\n\nGet your own College Reality Check at https://www.collegeunlock.com/ai-counseling`;

  const openCard = () => {
    if (!opened) {
      setOpened(true);
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'ai_fit_share_card_created', { has_dream: !!dream, has_target: !!target, has_safe: !!safe });
      }
    }
  };

  const trackShare = (method) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'ai_fit_share_card_shared', { method });
    }
  };

  const handleDownload = async () => {
    openCard(); setBusy(true);
    try {
      const blob = await generateFitShareImage({ dream, target, safe, tagline });
      trackShare('download');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'my-college-fit-check.png';
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { /* silent */ } finally { setBusy(false); }
  };

  const handleNativeShare = async () => {
    openCard(); setBusy(true);
    try {
      const blob = await generateFitShareImage({ dream, target, safe, tagline });
      if (navigator.share && navigator.canShare) {
        const file = new File([blob], 'my-college-fit-check.png', { type: 'image/png' });
        const shareData = { title: 'My College Fit Check', text: shareText, files: [file] };
        if (navigator.canShare(shareData)) { await navigator.share(shareData); trackShare('native'); return; }
      }
      await handleDownload();
    } catch { /* cancelled */ } finally { setBusy(false); }
  };

  const handleCopy = async () => {
    openCard();
    try {
      await navigator.clipboard.writeText(shareText);
      trackShare('copy');
    } catch { /* silent */ }
  };

  return (
    <div className="ct-share-fit-wrap" data-testid="ai-fit-share-card">
      <h4 className="ct-share-fit-title">Share My College Fit Check</h4>
      <p className="ct-share-fit-sub">Download or share your Dream / Target / Safe list.</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button onClick={handleDownload} disabled={busy} className="ct-share-fit-btn" data-testid="ai-fit-share-download">📥 Download</button>
        <button onClick={handleNativeShare} disabled={busy} className="ct-share-fit-btn" data-testid="ai-fit-share-native" style={{ background: '#D4A24C', color: '#0B2352' }}>Share</button>
        <button onClick={handleCopy} className="ct-share-fit-btn" data-testid="ai-fit-share-copy" style={{ background: '#fff', color: '#0B2352', border: '1.5px solid #0B2352' }}>Copy Text</button>
      </div>
    </div>
  );
}
